// Legal & policy content for PLAN FIRST NSW.
// IMPORTANT: These documents are drafts prepared for Australian legal review.
// They are not represented as legally approved documents.

export const NOTICE_VERSION = "pfn-notices-2026-10";

export const EDUCATIONAL_ACK =
  "I understand that Plan First NSW provides educational information and AI-assisted guidance, not professional planning or legal advice. I understand that generated information may require verification with council, a certifier or another qualified professional before I act on it.";

export const PRIVACY_ACK =
  "I have read the Document Upload & Data Handling Notice and Privacy Policy, and I consent to my uploaded documents being processed — including by third-party AI services — to generate my educational roadmap.";

export const LEGAL_PAGES = {
  terms: {
    title: "Terms & Conditions",
    intro:
      "These terms govern your use of the Plan First NSW website and the purchase of digital products and services. They are designed to be read alongside our Educational Information Disclaimer and Digital Products & Refunds Policy.",
    sections: [
      {
        h: "What Plan First NSW provides",
        body: [
          "Plan First NSW is an independent NSW planning education, information and self-guided preparation platform. We provide accessible information, educational toolkits, interactive workbooks, preparation checklists, explanatory resources and AI-assisted information tools.",
          "Our purpose is education, awareness and preparation — not the provision of professional planning, legal, engineering, building certification or other regulated professional advice. Plan First NSW does not replace council, statutory authorities, registered certifiers, planning consultants, lawyers, engineers, architects or other qualified advisers.",
        ],
      },
      {
        h: "Using this website",
        body: [
          "You agree to use this website lawfully and not to misuse, copy at scale, or interfere with the platform. Content is provided for general educational purposes and may change as NSW planning legislation, policies and council requirements change.",
          "Nothing on this website constitutes development consent, an approval, a certificate or confirmation of compliance with statutory requirements.",
        ],
      },
      {
        h: "Purchases",
        body: [
          "Digital products and services are described on the relevant pages, with prices shown in Australian dollars inclusive of GST. Payment is processed securely by Stripe. Delivery of digital products occurs electronically after payment is confirmed.",
          "Consultation bookings and any professional services offered separately have their own service descriptions, fees, scope of work and engagement terms. Purchasing a toolkit or AI-assisted roadmap does not include personal professional advice.",
        ],
      },
      {
        h: "Your consumer rights",
        body: [
          "Nothing in these terms excludes, restricts or modifies any consumer guarantee, right or remedy you have under the Australian Consumer Law or any other law that cannot lawfully be excluded.",
        ],
      },
      {
        h: "Liability",
        body: [
          "To the extent permitted by law, Plan First NSW is not liable for decisions made in reliance on general educational information without independent verification. You remain responsible for confirming current requirements with the relevant consent authority, council, registered certifier or appropriately qualified professional before making decisions or undertaking development.",
        ],
      },
      {
        h: "General",
        body: [
          "These terms are governed by the laws of New South Wales, Australia. If any part of these terms is found unenforceable, the remainder continues to apply. Questions: hello@planfirstnsw.com.au.",
        ],
      },
    ],
  },
  privacy: {
    title: "Privacy Policy",
    intro:
      "This policy explains what personal information Plan First NSW collects, why, how it is stored, and the choices you have. It reflects how the platform actually operates.",
    sections: [
      {
        h: "What we collect",
        body: [
          "Contact and enquiry details you submit (name, email, phone, suburb/council, message).",
          "Booking details for consultations (service selected, property and project details you provide).",
          "Newsletter subscription email addresses.",
          "Chat conversations with our AI planning assistant, and any contact details you choose to leave in chat.",
          "Documents you upload to the post-approval roadmap service — typically your development consent and any optional supporting documents — along with the property details you enter.",
          "Payment information is collected and processed by Stripe; we do not see or store your full card details.",
        ],
      },
      {
        h: "How we use it",
        body: [
          "To respond to enquiries, manage bookings, deliver purchases and provide the services you request.",
          "To generate your educational development roadmap from documents you upload.",
          "To understand, in aggregate, what visitors ask about so we can improve our educational resources.",
          "We do not sell your personal information.",
        ],
      },
      {
        h: "How documents are processed",
        body: [
          "Uploaded PDFs are stored in our hosted cloud environment and analysed to produce your report. Text is extracted from your document and processed by a third-party AI provider (accessed via our platform's integration service) solely to generate your roadmap. Under the AI provider's API terms, content submitted through the API is not used to train their models.",
          "Access to uploaded documents is restricted to Plan First NSW. We do not share your documents with other customers or advertisers.",
        ],
      },
      {
        h: "Storage, retention and deletion",
        body: [
          "Our website and database are hosted on a cloud application platform, and data may be stored on servers located outside Australia.",
          "We keep information for as long as reasonably needed to provide the service and meet record-keeping obligations. You can ask us to delete your uploaded documents, chat history or personal information at any time by emailing hello@planfirstnsw.com.au, and we will action genuine requests within a reasonable period.",
        ],
      },
      {
        h: "Your rights and questions",
        body: [
          "You may request access to, or correction of, the personal information we hold about you. This policy is intended to be consistent with the Australian Privacy Act 1988 (Cth). Contact: hello@planfirstnsw.com.au.",
        ],
      },
    ],
  },
  disclaimer: {
    title: "Educational Information Disclaimer",
    intro: "Important Information — Educational Use Only.",
    sections: [
      {
        h: "Educational use only",
        body: [
          "Plan First NSW is an independent planning education and information platform.",
          "All information, downloadable guides, interactive workbooks, checklists, AI-assisted tools and generated reports are provided for general educational and preliminary information purposes only.",
          "The platform does not provide professional town planning advice, legal advice, building certification advice, engineering advice or official statutory determinations.",
        ],
      },
      {
        h: "No definitive assessment",
        body: [
          "Information provided through the platform should not be treated as a definitive assessment of a property's development potential, planning permissibility, statutory obligations or compliance requirements.",
          "NSW planning legislation, policies, environmental constraints and local council requirements vary between properties and may change over time.",
        ],
      },
      {
        h: "Your responsibility",
        body: [
          "Users remain responsible for checking current requirements with the relevant consent authority, council, registered certifier or appropriately qualified professional before making decisions or undertaking development.",
          "Nothing provided through the platform constitutes development consent, an approval, a certificate or confirmation of compliance with statutory requirements.",
        ],
      },
      {
        h: "Independence",
        body: [
          "Plan First NSW is not affiliated with or endorsed by the NSW Government, the NSW Planning Portal or any local council.",
          "These notices do not exclude or restrict any rights customers have under Australian Consumer Law.",
        ],
      },
    ],
  },
  "ai-disclaimer": {
    title: "AI-Assisted Tools Disclaimer",
    intro:
      "Plan First NSW uses AI-assisted technology in its planning chat assistant and its post-approval development roadmap service. This page explains what that means — and what it does not mean.",
    sections: [
      {
        h: "What our AI tools do",
        body: [
          "Help you navigate publicly available NSW planning information and explain terminology and development processes in plain English.",
          "Organise and summarise documents you choose to upload, and identify topics and requirements that may need further investigation.",
          "Generate preliminary educational checklists and questions, and direct you to official government or council resources where verified links are available.",
          "Help you prepare for discussions with qualified professionals.",
        ],
      },
      {
        h: "What our AI tools do not do",
        body: [
          "AI-generated content is AI-assisted educational material. It is not professional planning, legal or certification advice, and it is not a substitute for a qualified planning assessment.",
          "We do not claim AI-generated information is professionally reviewed, independently verified or legally authoritative unless that review has actually occurred.",
          "AI-generated information may be incomplete or inaccurate. Where information is missing, ambiguous or cannot be verified, our tools are designed to say so and direct you to seek clarification from the relevant authority or professional.",
        ],
      },
      {
        h: "Sources",
        body: [
          "When citing legislation or planning requirements, our tools prioritise authoritative NSW Government and local council sources. Where a matter is council-specific, it is flagged for verification rather than guessed.",
        ],
      },
    ],
  },
  refunds: {
    title: "Digital Products & Refunds Policy",
    intro:
      "This policy covers digital toolkits (PDF downloads) and the AI-assisted development roadmap service. It preserves all of your rights under the Australian Consumer Law (ACL).",
    sections: [
      {
        h: "Digital toolkits",
        body: [
          "Toolkits are digital PDF products delivered electronically immediately after payment is confirmed. Because of their digital nature, change-of-mind refunds are generally not offered once files have been downloaded.",
          "If a file fails to download or is corrupted, contact us and we will restore your access or refund you.",
        ],
      },
      {
        h: "Development roadmap service",
        body: [
          "The post-approval roadmap is an individualised, AI-assisted educational report generated from the consent you upload. If we cannot read or analyse your document reliably, we will tell you rather than guess — and you receive a full refund.",
        ],
      },
      {
        h: "Your Australian Consumer Law rights",
        body: [
          "Our products and services come with guarantees that cannot be excluded under the Australian Consumer Law. You may be entitled to a replacement or refund for a major problem, and compensation for other reasonably foreseeable loss or damage. Nothing in this policy limits those rights.",
          "To request a refund or raise an issue, email hello@planfirstnsw.com.au with your order details. We aim to respond within two business days.",
        ],
      },
    ],
  },
  "document-upload": {
    title: "Document Upload & Data Handling Notice",
    intro:
      "This notice explains exactly what happens when you upload your development consent to the post-approval roadmap service.",
    sections: [
      {
        h: "What you upload",
        body: [
          "Your development consent PDF (required) and, optionally, supporting documents such as approved plans, modified consents or council correspondence, plus the property details you type into the form.",
        ],
      },
      {
        h: "How it is processed",
        body: [
          "Your PDF is uploaded over an encrypted connection and stored in our hosted cloud environment. Text is extracted from the document and processed by a third-party AI provider through our platform's integration service, solely to identify and explain your conditions and generate your roadmap. Under the provider's API terms, API content is not used to train their models.",
          "If your document cannot be read reliably (for example, a scan without selectable text), we do not guess — the report is paused and we offer a manual review path instead.",
        ],
      },
      {
        h: "Who can access it",
        body: [
          "Access to uploaded documents is restricted to Plan First NSW. Documents are never shared with other customers, sold, or used for advertising.",
        ],
      },
      {
        h: "Retention and deletion",
        body: [
          "We retain uploaded documents only as long as reasonably needed to provide the service and support your report. You can request deletion of your documents and report at any time via hello@planfirstnsw.com.au and we will action genuine requests within a reasonable period.",
        ],
      },
    ],
  },
  ip: {
    title: "Intellectual Property & Permitted Use",
    intro: "The content on this platform and in our products is protected by Australian copyright law.",
    sections: [
      {
        h: "Our content",
        body: [
          "All website content, guides, toolkits, workbooks, branding and generated report formats are the intellectual property of Plan First NSW (or used with permission).",
        ],
      },
      {
        h: "Your licence when you purchase",
        body: [
          "Purchasing a digital toolkit grants you a non-exclusive, non-transferable licence for your own personal use — including printing a copy for your own records and completing fillable workbooks for your own project.",
          "You must not resell, redistribute, publish, share publicly, or upload our products to file-sharing or AI training services.",
        ],
      },
      {
        h: "Your documents",
        body: [
          "You retain all rights in documents you upload. You grant us only the limited right to process them to provide the service you requested.",
        ],
      },
    ],
  },
};

export const LEGAL_NAV = [
  ["terms", "Terms & Conditions"],
  ["privacy", "Privacy Policy"],
  ["disclaimer", "Educational Disclaimer"],
  ["ai-disclaimer", "AI Tools Disclaimer"],
  ["refunds", "Digital Products & Refunds"],
  ["document-upload", "Document Upload Notice"],
  ["ip", "Intellectual Property"],
];
